import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Instagram, Linkedin } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { BRAND, NAV_LINKS } from "../lib/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-lg border-b border-black/5" : "bg-white/0"
      }`}
    >
      <div className="container-tight flex items-center justify-between h-[140px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img src="/assets/logo.png" alt="My Hoom" className="h-28 sm:h-32 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-navy" : "text-navy/60 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <div className="flex items-center gap-1.5 mr-2">
            <a href={BRAND.socials.instagram} target="_blank" rel="noopener noreferrer" data-testid="nav-instagram" aria-label="Instagram" className="h-8 w-8 rounded-full text-navy/55 hover:text-gold hover:bg-navy/5 flex items-center justify-center transition-colors">
              <Instagram size={15} strokeWidth={1.8} />
            </a>
            <a href={BRAND.socials.linkedin} target="_blank" rel="noopener noreferrer" data-testid="nav-linkedin" aria-label="LinkedIn" className="h-8 w-8 rounded-full text-navy/55 hover:text-gold hover:bg-navy/5 flex items-center justify-center transition-colors">
              <Linkedin size={15} strokeWidth={1.8} />
            </a>
          </div>
          <a href={`tel:${BRAND.phoneINraw}`} data-testid="nav-phone" className="text-sm text-navy/70 hover:text-navy flex items-center gap-2">
            <Phone size={14} strokeWidth={2} />
            {BRAND.phoneIN}
          </a>
          <Link to="/contact" data-testid="nav-cta-consultation" className="btn-gold text-sm">
            Book a Consultation
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 -mr-2 text-navy"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.22, 0.61, 0.36, 1], duration: 0.35 }}
            data-testid="nav-mobile-drawer"
            className="lg:hidden fixed top-[140px] right-0 bottom-0 w-[88%] max-w-sm bg-white shadow-2xl border-l border-black/5 flex flex-col"
          >
            <div className="flex flex-col p-6 gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 border-b border-black/5 font-serif text-2xl ${
                      isActive ? "text-navy" : "text-navy/70"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
            <div className="mt-auto p-6 border-t border-black/5 space-y-3">
              <a href={`tel:${BRAND.phoneINraw}`} className="flex items-center gap-2 text-navy/80 text-sm">
                <Phone size={16} /> {BRAND.phoneIN}
              </a>
              <div className="flex items-center gap-3 pt-1">
                <a href={BRAND.socials.instagram} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-instagram" aria-label="Instagram" className="h-10 w-10 rounded-full border border-black/10 text-navy/70 hover:text-gold hover:border-gold flex items-center justify-center transition-colors">
                  <Instagram size={16} />
                </a>
                <a href={BRAND.socials.linkedin} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-linkedin" aria-label="LinkedIn" className="h-10 w-10 rounded-full border border-black/10 text-navy/70 hover:text-gold hover:border-gold flex items-center justify-center transition-colors">
                  <Linkedin size={16} />
                </a>
              </div>
              <Link to="/contact" data-testid="nav-mobile-cta" className="btn-gold w-full">
                Book a Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
